import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from "@iconify/react";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Header from '../Header';
import { getUser, clearUser } from '../../helpers/sessionHelper';
import Swal from 'sweetalert2';
import Common from '../../helpers/common';
import { respEnum, PaymentStatusEnum, BookingStatusEnum } from '../../constants/appConstants';
import '../../assets/css/style.css';
import Footer from '../Footer';
const Dashboard = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    document.title = "User Dashboard | Ignite";
  }, []);

  // Bookings state
  const [bookingsData, setBookingsData] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsPagination, setBookingsPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
    isMore: false
  });

  // Modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);

  // Filters state
  const [bookingsFilters, setBookingsFilters] = useState({
    fromDateTime: '',
    endDateTime: '',
    courtId: null,
    sportId: null,
    bookingStatus: null,
    paymentStatus: null
  });

  // Function to get user data using sessionHelper
  const getUserData = () => {
    try {
      const user = getUser();
      if (user) {
        setUserData(user);
        return user;
      } else {
        // No login data, redirect to home
        navigate('/');
        return null;
      }
    } catch (error) {
      // console.error('Error getting user data:', error);
      navigate('/');
      return null;
    }
  };

  // Function to handle logout with confirmation using sessionHelper
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel',
      background: '#1a1a1a',
      color: '#ffffff',
      customClass: {
        popup: 'swal-dark-popup',
        title: 'swal-dark-title',
        content: 'swal-dark-content'
      }
    });

    if (result.isConfirmed) {
      await clearUser();
      setUserData(null);

      // Show success message
      await Swal.fire({
        title: 'Logged Out',
        text: 'You have been successfully logged out!',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        background: '#1a1a1a',
        color: '#ffffff',
        customClass: {
          popup: 'swal-dark-popup'
        }
      });

      navigate('/');
    }
  };

  // Get display name
  const getDisplayName = () => {
    if (userData) {
      const firstName = userData.firstName || '';
      const lastName = userData.lastName || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (firstName) {
        return firstName;
      } else if (userData.email) {
        return userData.email.split('@')[0];
      }
    }
    return 'User';
  };

  // Function to fetch bookings data
  const fetchBookings = async (pageNumber = 1, filters = bookingsFilters) => {
    try {
      setBookingsLoading(true);

      // Prepare API parameters
      const params = {
        pageNumber: pageNumber,
        recordsToDisplay: bookingsPagination.pageSize,
        ...filters
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await Common.ApiService.getInstance().request(
        'GetMyBookings',
        params,
        'POST',
        true,
        true,
        true
      );

      if (response && response.status === respEnum.Success && response.data && response.data.length > 0) {
        const paginationData = response.data[0];
        setBookingsData(paginationData.data || []);
        setBookingsPagination({
          pageNumber: paginationData.pageNumber,
          pageSize: paginationData.pageSize,
          totalRecords: paginationData.totalRecords,
          totalPages: paginationData.totalPages,
          isMore: paginationData.isMore
        });
      } else {
        setBookingsData([]);
        setBookingsPagination(prev => ({ ...prev, totalRecords: 0, totalPages: 0 }));
      }
    } catch (error) {
      // console.error('Error fetching bookings:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load bookings data',
        background: '#1a1a1a',
        color: '#ffffff'
      });
    } finally {
      setBookingsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...bookingsFilters, [filterName]: value };
    setBookingsFilters(newFilters);
    fetchBookings(1, newFilters); // Reset to page 1 when filtering
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= bookingsPagination.totalPages) {
      fetchBookings(newPage, bookingsFilters);
    }
  };

  // Function to fetch complete profile data
  const fetchProfileData = async () => {
    try {
      setIsLoading(true);

      const response = await Common.ApiService.getInstance().request(
        'GetMyProfile',
        {},
        'Get',
        true,
        true,
        true
      );

      if (response && response.status === respEnum.Success && response.data && response.data.length > 0) {
        const profileData = response.data[0];
        setUserData(profileData);

        // Also update session storage with fresh data
        sessionStorage.setItem('userData', JSON.stringify(profileData));
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load profile data',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while loading profile data',
        background: '#1a1a1a',
        color: 'black',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check user data on component mount and fetch bookings when viewing bookings
  useEffect(() => {
    getUserData();
  }, []);

  // Fetch profile data when Account Details tab is accessed
  useEffect(() => {
    if (activeView === 'account') {
      fetchProfileData();
    }
  }, [activeView]);

  // Handle URL query parameters for tab switching
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get('tab');
    if (tab === 'bookings') {
      setActiveView('bookings');
      // Clear the URL parameter after setting the tab
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (activeView === 'bookings' && userData) {
      fetchBookings();
    }
  }, [activeView, userData]);

  // Utility functions
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateTimeString;
    }
  };

  const formatTime = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return '-';
    try {
      const start = new Date(startDateTime);
      const end = new Date(endDateTime);
      const startTime = start.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const endTime = end.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${startTime} - ${endTime}`;
    } catch (error) {
      return '-';
    }
  };

  const getBookingStatusColor = (bookingStatus) => {
    if (bookingStatus === BookingStatusEnum.Booked) return '#28a745'; // Booked - Green
    if (bookingStatus === BookingStatusEnum.Cancelled_By_Club) return '#dc3545'; // Cancelled - Red
    return '#ffc107'; // Default - Yellow
  };

  const getBookingStatusText = (bookingStatus) => {
    if (bookingStatus === BookingStatusEnum.Booked) return 'Booked';
    if (bookingStatus === BookingStatusEnum.Cancelled_By_Club) return 'Cancelled';
    return 'Unknown';
  };

  const getPaymentStatusColor = (paymentStatus) => {
    if (paymentStatus === PaymentStatusEnum.Paid) return '#28a745'; // Paid - Green
    if (paymentStatus === PaymentStatusEnum.Partial_Paid) return '#ffc107'; // Partial - Yellow
    if (paymentStatus === PaymentStatusEnum.Un_Paid) return '#dc3545'; // Unpaid - Red
    return '#6c757d'; // Default - Gray
  };

  const getPaymentStatusText = (paymentStatus) => {
    if (paymentStatus === PaymentStatusEnum.Paid) return 'Paid';
    if (paymentStatus === PaymentStatusEnum.Partial_Paid) return 'Partial Paid';
    if (paymentStatus === PaymentStatusEnum.Un_Paid) return 'Unpaid';
    return 'Unknown';
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string(),
    gender: Yup.number(),
    dob: Yup.date()
  });

  // Update Profile Function
  const handleUpdateProfile = async (values) => {
    try {
      setIsLoading(true);

      const updateData = {
        firstName: values.firstName,
        lastName: values.lastName,
        gender: values.gender ? parseInt(values.gender) : null,
        dob: values.dob || null,
        image: values.image || null,
        cityId: values.cityId || null,
        countryId: values.countryId || null,
        stateId: values.stateId || null
      };

      const response = await Common.ApiService.getInstance().request(
        'UpdateProfile',
        updateData,
        'POST',
        true,
        true,
        true
      );

      if (response && response.status === respEnum.Success) {
        await Swal.fire({
          icon: 'success',
          title: 'Profile Updated',
          text: response.message || 'Profile updated successfully!',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#28a745'
        });

        // Refresh user data
        fetchProfileData();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: response?.message || 'Failed to update profile. Please try again.',
          background: '#1a1a1a',
          color: '#fff',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Update profile error:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while updating profile. Please try again.',
        background: '#1a1a1a',
        color: '#fff',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Booking Details Modal Component
  const BookingDetailsModal = ({ show, onClose, booking }) => {
    if (!show || !booking) return null;

    return (
      <div
        className="modal fade show"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(5px)',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050
        }}
        onClick={onClose}
      >
        <div
          className="modal-dialog"
          style={{
            maxWidth: '700px',
            width: '95%',
            margin: '0'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-content"
            style={{
              overflowY: 'auto',
              height: '90vh',
              backgroundColor: '#1a1a1a',
              border: '2px solid #28a745',
              borderRadius: '15px',
              boxShadow: '0 20px 40px rgba(40, 167, 69, 0.3)',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#000000'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#000000!important',
                borderRadius: '4px'
              }
            }}
          >
            {/* Header */}
            <div
              className="modal-header text-center"
              style={{
                marginTop: '900px!important',
                backgroundColor: '#28a745',
                borderBottom: 'none',
                borderRadius: '13px 13px 0 0',
                padding: '20px 15px'
              }}
            >
              <div className="w-100 text-center">
                <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📋</div>
                <h3 className="modal-title text-white mb-0" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                  Booking Details
                </h3>
                <p className="text-white-50 mb-0" style={{ fontSize: '14px' }}>
                  Complete information about your booking
                </p>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                onClick={onClose}
                style={{ fontSize: '20px' }}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body" style={{ padding: '20px 15px', color: '#ffffff' ,marginTop: '900px!important'}}>
              {/* Booking Reference */}
              <div className="text-center mb-3">
                <div
                  style={{
                    backgroundColor: '#2a2a2a',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #3d3d3d'
                  }}
                >
                  <small style={{ color: '#aaa', fontSize: '18px' }}>Ref #</small>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745', marginTop: '4px' }}>
                    {booking.refNo}
                  </div>
                </div>
              </div>

              {/* Booking Details Grid */}
              <div className="row g-2">
                {/* Court & Sport Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: '#2a2a2a',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #3d3d3d',
                      height: '100%'
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>🏟️</span>
                      <h6 className="mb-0" style={{ color: '#28a745', fontSize: '14px' }}>Court Details</h6>
                    </div>
                    <div className="mb-2">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Court</small>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{booking.courtName || 'N/A'}</div>
                    </div>
                    <div className="mb-2">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Sport</small>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{booking.sportName || 'N/A'}</div>
                    </div>
                    <div>
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Players</small>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{booking.noofplayers || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Date & Time Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: '#2a2a2a',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #3d3d3d',
                      height: '100%'
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>📅</span>
                      <h6 className="mb-0" style={{ color: '#28a745', fontSize: '14px' }}>Schedule</h6>
                    </div>
                    <div className="mb-2">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Date</small>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{formatDateTime(booking.startDateTime)}</div>
                    </div>
                    <div>
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Time Slot</small>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{formatTime(booking.startDateTime, booking.endDateTime)}</div>
                    </div>
                  </div>
                </div>

                {/* Status Info */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: '#2a2a2a',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #3d3d3d',
                      height: '100%'
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>📊</span>
                      <h6 className="mb-0" style={{ color: '#28a745', fontSize: '14px' }}>Status</h6>
                    </div>
                    <div className="mb-2">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Booking Status</small>
                      <div>
                        <span
                          style={{
                            backgroundColor: getBookingStatusColor(booking.bookingStatus),
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          {getBookingStatusText(booking.bookingStatus)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Payment Status</small>
                      <div>
                        <span
                          style={{
                            backgroundColor: getPaymentStatusColor(booking.paymentStatus),
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}
                        >
                          {getPaymentStatusText(booking.paymentStatus)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amount Details */}
                <div className="col-md-6">
                  <div
                    style={{
                      backgroundColor: '#2a2a2a',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #3d3d3d',
                      height: '100%'
                    }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>💰</span>
                      <h6 className="mb-0" style={{ color: '#28a745', fontSize: '14px' }}>Amount Details</h6>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Court Charges:</small>
                      <span style={{ fontWeight: '600', fontSize: '12px' }}>Rs. {booking.courtcharges || booking.total || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Discount:</small>
                      <span style={{ fontWeight: '600', color: booking.discountAmount > 0 ? '#28a745' : '#666', fontSize: '12px' }}>
                        -Rs. {booking.discountAmount || 0}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Platform Fee:</small>
                      <span style={{ fontWeight: '600', fontSize: '12px' }}>Rs. {booking.platformfee || 0}</span>
                    </div>
                    <hr style={{ borderColor: '#3d3d3d', margin: '8px 0' }} />
                    <div className="d-flex justify-content-between mb-1">
                      <strong style={{ color: '#28a745', fontSize: '12px' }}>Total Amount:</strong>
                      <strong style={{ color: '#28a745', fontSize: '13px' }}>Rs. {booking.total || booking.courtcharges || 0}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Paid Amount:</small>
                      <span style={{ fontWeight: '600', color: '#28a745', fontSize: '12px' }}>Rs. {booking.receivedAmount || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <small style={{ color: '#aaa', fontSize: '11px' }}>Remaining:</small>
                      <span style={{ fontWeight: '600', color: '#ffc107', fontSize: '12px' }}>
                        Rs. {booking.remainingAmount || ((booking.total || booking.courtcharges || 0) - (booking.receivedAmount || 0)) || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="modal-footer justify-content-center"
              style={{
                borderTop: '1px solid #3d3d3d',
                padding: '15px'
              }}
            >
              <button
                type="button"
                className="btn btn-success px-4"
                onClick={onClose}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  fontWeight: '600',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontSize: '14px'
                }}
              >
                <span style={{ marginRight: '6px' }}>✓</span>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDashboardView = () => (
    <div className="cs_dashboard_content">
      <div className="cs_dashboard_welcome">
        <h3>Welcome {getDisplayName()}
          {/* (not you? <Link to="#" onClick={handleLogout}>Log out</Link>) */}
        </h3>
        {/* <p>From your account dashboard you can view your recent bookings and edit your password and account details.</p> */}
      </div>

      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="cs_dashboard_card" onClick={() => setActiveView('bookings')}>
            <Icon icon="bx:calendar" className="dashboard_icon" />
            <h6>BOOKINGS</h6>
          </div>
        </div>

        <div className="col-md-4 mb-4">
          <div className="cs_dashboard_card" onClick={() => setActiveView('account-details')}>
            <Icon icon="bx:user" className="dashboard_icon" />
            <h6>ACCOUNT DETAILS</h6>
          </div>
        </div>

        <div className="col-md-4 mb-4">
          <div
            className="cs_dashboard_card"
            onClick={handleLogout}
            style={{ cursor: 'pointer' }}
          >
            <Icon icon="bx:lock" className="dashboard_icon" />
            <h6>LOGOUT</h6>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBookingsView = () => (
    <div className="cs_dashboard_content">
      <h2 style={{ marginBottom: '30px' }}>My Bookings</h2>

      {/* Filters Section */}
      <div className="cs_bookings_filters" style={{
        marginBottom: '20px',
        padding: '20px',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        border: '1px solid #3d3d3d'
      }}>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label style={{ marginBottom: '8px', display: 'block', color: '#ffffff' }}>
              From Date
            </label>
            <input
              type="date"
              value={bookingsFilters.fromDateTime}
              onChange={(e) => handleFilterChange('fromDateTime', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #3d3d3d',
                borderRadius: '4px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>
          <div className="col-md-4 mb-3">
            <label style={{ marginBottom: '8px', display: 'block', color: '#ffffff' }}>
              To Date
            </label>
            <input
              type="date"
              value={bookingsFilters.endDateTime}
              onChange={(e) => handleFilterChange('endDateTime', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #3d3d3d',
                borderRadius: '4px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>
          <div className="col-md-4 mb-3">
            <label style={{ marginBottom: '8px', display: 'block', color: '#ffffff' }}>
              Booking Status
            </label>
            <select
              value={bookingsFilters.bookingStatus || ''}
              onChange={(e) => handleFilterChange('bookingStatus', e.target.value ? parseInt(e.target.value) : null)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #3d3d3d',
                borderRadius: '4px',
                color: 'white',
                fontSize: '14px'
              }}
            >
              <option value="">All Status</option>
              <option value={BookingStatusEnum.Booked}>Booked</option>
              <option value={BookingStatusEnum.Cancelled_By_Club}>Cancelled</option>
            </select>
          </div>
          <div className="col-md-4 mb-3">
            <label style={{ marginBottom: '8px', display: 'block', color: '#ffffff' }}>
              Payment Status
            </label>
            <select
              value={bookingsFilters.paymentStatus || ''}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value ? parseInt(e.target.value) : null)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#2a2a2a',
                border: '1px solid #3d3d3d',
                borderRadius: '4px',
                color: 'white',
                fontSize: '14px'
              }}
            >
              <option value="">All Payments</option>
              <option value={PaymentStatusEnum.Paid}>Paid</option>
              <option value={PaymentStatusEnum.Partial_Paid}>Partial Paid</option>
              <option value={PaymentStatusEnum.Un_Paid}>Unpaid</option>
            </select>
          </div>

        </div>
        <div className="row">
          <div className="col-md-12">
            <button
              onClick={() => {
                setBookingsFilters({
                  fromDateTime: '',
                  endDateTime: '',
                  courtId: null,
                  sportId: null,
                  bookingStatus: null,
                  paymentStatus: null
                });
                fetchBookings(1, {
                  fromDateTime: '',
                  endDateTime: '',
                  courtId: null,
                  sportId: null,
                  bookingStatus: null,
                  paymentStatus: null
                });
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {bookingsLoading && (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ color: 'black' }}>Loading bookings...</div>
        </div>
      )}

      {/* Bookings Table */}
      {!bookingsLoading && (
        <>
          <div className="cs_bookings_table" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #3d3d3d' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Court</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Sport</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Time</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Booking Status</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Payment Status</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Amount</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Remaining</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#ffffff' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookingsData.map((booking) => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid #3d3d3d' }}>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>{booking.courtName || '-'}</td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>{booking.sportName || '-'}</td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>{formatDateTime(booking.startDateTime)}</td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>{formatTime(booking.startDateTime, booking.endDateTime)}</td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        backgroundColor: getBookingStatusColor(booking.bookingStatus),
                        color: 'white',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {getBookingStatusText(booking.bookingStatus)}
                      </span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{
                        backgroundColor: getPaymentStatusColor(booking.paymentStatus),
                        color: 'white',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {getPaymentStatusText(booking.paymentStatus)}
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>
                      Rs. {booking.total || booking.courtcharges || 0}
                    </td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#ffffff' }}>
                      Rs. {booking.remainingAmount || ((booking.total || booking.courtcharges || 0) - (booking.receivedAmount || 0)) || 0}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <button
                        className="cs_btn cs_style_1"
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          color: 'white',
                          fontWeight: 600
                        }}
                        onClick={() => {
                          setSelectedBookingDetails(booking);
                          setShowBookingModal(true);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {bookingsPagination.totalPages > 1 && (
            <div style={{
              width: '100%',
              overflowX: 'auto',
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px',
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              border: '1px solid #3d3d3d'
            }}>
              <div style={{ color: '#ffffff' }}>
                Showing {((bookingsPagination.pageNumber - 1) * bookingsPagination.pageSize) + 1} to {Math.min(bookingsPagination.pageNumber * bookingsPagination.pageSize, bookingsPagination.totalRecords)} of {bookingsPagination.totalRecords} entries
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handlePageChange(bookingsPagination.pageNumber - 1)}
                  disabled={bookingsPagination.pageNumber <= 1}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: bookingsPagination.pageNumber <= 1 ? '#3d3d3d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: bookingsPagination.pageNumber <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, bookingsPagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (bookingsPagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (bookingsPagination.pageNumber <= 3) {
                    pageNum = i + 1;
                  } else if (bookingsPagination.pageNumber >= bookingsPagination.totalPages - 2) {
                    pageNum = bookingsPagination.totalPages - 4 + i;
                  } else {
                    pageNum = bookingsPagination.pageNumber - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: pageNum === bookingsPagination.pageNumber ? '#007bff' : '#3d3d3d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(bookingsPagination.pageNumber + 1)}
                  disabled={bookingsPagination.pageNumber >= bookingsPagination.totalPages}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: bookingsPagination.pageNumber >= bookingsPagination.totalPages ? '#3d3d3d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: bookingsPagination.pageNumber >= bookingsPagination.totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* No data message */}
          {bookingsData.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              border: '1px solid #3d3d3d',
              marginTop: '20px'
            }}>
              <p style={{ color: '#ffffff', fontSize: '16px', marginBottom: '0' }}>
                No bookings found matching your criteria.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderAccountDetailsView = () => (
    <div className="cs_dashboard_content">
      <h2 style={{ marginBottom: '30px' }}>Account details</h2>

      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          flexDirection: 'column',
          gap: '15px'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #3d3d3d',
            borderTop: '4px solid #28a745',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#fff', fontSize: '16px' }}>Loading profile data...</p>
        </div>
      ) : (
        <Formik
          initialValues={{
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
            email: userData?.email || '',
            phone: userData?.phone || '',
            phoneCode: userData?.phoneCode || '+92',
            gender: userData?.gender || '',
            dob: userData?.dob ? new Date(userData.dob).toISOString().split('T')[0] : ''
          }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleUpdateProfile}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <div className="row">
                {/* First Name */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      First name *
                    </label>
                    <Field
                      name="firstName"
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #3d3d3d',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    />
                    {errors.firstName && touched.firstName && (
                      <div style={{ color: '#ff4d4d', fontSize: '12px', marginTop: '5px' }}>{errors.firstName}</div>
                    )}
                  </div>
                </div>

                {/* Last Name */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      Last name *
                    </label>
                    <Field
                      name="lastName"
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #3d3d3d',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    />
                    {errors.lastName && touched.lastName && (
                      <div style={{ color: '#ff4d4d', fontSize: '12px', marginTop: '5px' }}>{errors.lastName}</div>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      Email address
                    </label>
                    <Field
                      disabled={true}
                      name="email"
                      type="email"
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid #3d3d3d',
                        borderRadius: '4px',
                        color: '#999',
                        cursor: 'not-allowed'
                      }}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      Phone number
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* Country Code */}
                      <Field
                        disabled={true}
                        name="phoneCode"
                        placeholder="+92"
                        style={{
                          width: '80px',
                          padding: '10px',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #3d3d3d',
                          borderRadius: '4px',
                          color: '#999',
                          cursor: 'not-allowed',
                          textAlign: 'center'
                        }}
                      />
                      {/* Phone Number */}
                      <Field
                        disabled={true}
                        name="phone"
                        type="tel"
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #3d3d3d',
                          borderRadius: '4px',
                          color: '#999',
                          cursor: 'not-allowed'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Gender */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      Gender
                    </label>
                    <Field
                      as="select"
                      name="gender"
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #3d3d3d',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    >
                      <option value="">Select Gender</option>
                      <option value="1">Male</option>
                      <option value="2">Female</option>
                      <option value="3">Other</option>
                    </Field>
                    {errors.gender && touched.gender && (
                      <div style={{ color: '#ff4d4d', fontSize: '12px', marginTop: '5px' }}>{errors.gender}</div>
                    )}
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="col-md-6">
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ marginBottom: '8px', display: 'block', color: '#fff' }}>
                      Date of Birth
                    </label>
                    <Field
                      name="dob"
                      type="date"
                      max={new Date().toISOString().split('T')[0]} // Disable future dates
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #3d3d3d',
                        borderRadius: '4px',
                        color: 'white'
                      }}
                    />
                    {errors.dob && touched.dob && (
                      <div style={{ color: '#ff4d4d', fontSize: '12px', marginTop: '5px' }}>{errors.dob}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Update Button */}
              <div style={{ marginTop: '30px' }}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="cs_btn cs_style_1"
                  style={{
                    padding: '12px 30px',
                    fontSize: '16px',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  {isLoading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );

  return (
    <>
      <Header
        logoUrl="/images/ignite_logo.png"
        colorVariant="cs_dark_color"
        actionBtnText="Getting Started"
        actionBtnUrl="/contact"
      />
      <style jsx="true">{`
        h3, h2, h4, h5, h6 {
          clear: both;
          color: #ffffff;
        }
        
        .cs_dashboard_wrapper {
          background-color: #1a1a1a;
          min-height: 100vh;
          color: white;
        }

        .dashboard_row {
          padding: 40px 0;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .cs_dashboard_sidebar {
          background-color: #2d2d2d;
          padding: 20px;
          border-radius: 8px;
        }

        .cs_dashboard_title {
          font-size: 24px;
          margin-bottom: 20px;
        }

        .cs_dashboard_menu {
          list-style: none;
          padding: 0;
        }

        .cs_dashboard_menu li a, .cs_dashboard_menu li span {
          color: white;
          padding: 10px 0;
          display: block;
          border-bottom: 1px solid #3d3d3d;
          cursor: pointer;
          text-decoration: none;
        }

        .cs_dashboard_menu li a.active, .cs_dashboard_menu li span.active {
          color: #00f3ff;
        }

        .cs_dashboard_menu li:last-child a, .cs_dashboard_menu li:last-child span {
          border-bottom: none;
        }

        .cs_dashboard_content {
          background-color: #2d2d2d;
          padding: 30px;
          border-radius: 8px;
        }

        .cs_dashboard_welcome {
          margin-bottom: 30px;
        }

        .cs_dashboard_welcome a {
          color: #00f3ff;
        }

        .cs_dashboard_card {
          background-color: #1a1a1a;
          border: 1px solid #01effb59;
          padding: 30px;
          border-radius: 8px;
          display: block;
          text-align: center;
          color: white;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cs_dashboard_card:hover {
          transform: translateY(-5px);
          border-color: #00f3ff;
        }

        .dashboard_icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        /* SweetAlert Dark Theme Styling */
        .swal-dark-popup {
          background-color: #1a1a1a !important;
          border: 1px solid #3d3d3d !important;
        }

        .swal-dark-title {
          color: #ffffff !important;
        }

        .swal-dark-content {
          color: #ffffff !important;
        }
       .cs_moving_text_wrap.cs_style_1 .cs_moving_text {
    margin-top: 100px;
            background-color: black;}
    .cs_moving_text_wrap.cs_style_1 {
    background-color: black;
    line-height: 1.2em;
    color: transparent;
    -webkit-text-stroke: 2px #ffffff;
}
        .cs_height_120 {
        background-color: black;}
      `}</style>

      <div className="cs_dashboard_wrapper">
        <div className="container">
          <div className="row dashboard_row">
            <div className="col-lg-3">
              <div className="cs_dashboard_sidebar">
                <h2 className="cs_dashboard_title">MY ACCOUNT</h2>
                <ul className="cs_dashboard_menu">
                  <li>
                    <span
                      className={activeView === 'dashboard' ? 'active' : ''}
                      onClick={() => setActiveView('dashboard')}
                    >
                      Dashboard
                    </span>
                  </li>
                  <li>
                    <span
                      className={activeView === 'bookings' ? 'active' : ''}
                      onClick={() => setActiveView('bookings')}
                    >
                      Bookings
                    </span>
                  </li>
                  <li>
                    <span
                      className={activeView === 'account-details' ? 'active' : ''}
                      onClick={() => setActiveView('account-details')}
                    >
                      Account details
                    </span>
                  </li>
                  <li>
                    <span
                      onClick={handleLogout}
                      style={{ cursor: 'pointer' }}
                    >
                      Log out
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-lg-9">
              {activeView === 'dashboard' && renderDashboardView()}
              {activeView === 'bookings' && renderBookingsView()}
              {activeView === 'account-details' && renderAccountDetailsView()}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Details Modal */}
      <BookingDetailsModal
        show={showBookingModal}
        onClose={() => {
          setShowBookingModal(false);
          setSelectedBookingDetails(null);
        }}
        booking={selectedBookingDetails}
      />
      <Footer  />
    </>
  );
};

export default Dashboard; 